import React, { useState, useEffect, useCallback } from 'react';
import useModalWindow from '../common/useModalWindow';
import { useTranslation } from 'react-i18next';
import { FiX, FiRefreshCw, FiMaximize2, FiMinimize2, FiPlus, FiEdit3, FiTrash2, FiLock, FiEye, FiFileText, FiTrendingUp, FiBarChart2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import QueryTemplateEditor from './QueryTemplateEditor';
import { useConfirm } from '../ConfirmDialog';
import styles from './MonitoringDashboardModal.module.css';

/**
 * 모니터링 > 쿼리 템플릿 전용 관리 모달 (최상위 메뉴에서 호출).
 *
 * - 내장 템플릿(builtin): 잠금 아이콘, "보기" 만 가능 (수정/삭제 불가)
 * - 사용자 템플릿: 편집 / 삭제 가능
 * - "+ 템플릿 추가" 버튼으로 새 템플릿 작성
 *
 * 추가된 템플릿은 PanelEditor 의 "템플릿" 드롭다운에 즉시 노출된다
 * (대시보드 모달이 열릴 때 /query-templates 를 재로드하므로 자동 반영).
 */

async function describeHttpError(res, fallback, tr) {
  let serverMsg = '';
  try { const body = await res.json(); serverMsg = body?.error || body?.message || ''; } catch { /* */ }
  const suffix = ` (HTTP ${res.status})`;
  if (res.status === 404) return (serverMsg || fallback) + tr('monitoring:qtm.err.mayNeedRestart', { suffix: suffix });
  return (serverMsg || fallback) + suffix;
}

const getDbTypeLabel = (t) => ({
  any: t('monitoring:qtm.generic'),
  oracle: 'Oracle',
  postgresql: 'PostgreSQL',
  mysql: 'MySQL',
  tibero: 'Tibero',
  sqlite: 'SQLite',
});

const getDbTypeFilterOptions = (t) => [
  { value: 'all',        label: t('monitoring:qtm.allDbms') },
  { value: 'any',        label: t('monitoring:qtm.genericAny') },
  { value: 'oracle',     label: 'Oracle' },
  { value: 'postgresql', label: 'PostgreSQL' },
  { value: 'mysql',      label: 'MySQL' },
  { value: 'tibero',     label: 'Tibero' },
  { value: 'sqlite',     label: 'SQLite' },
];

const getChartFilterOptions = (t) => [
  { value: 'all',  label: t('monitoring:qtm.all'), icon: null },
  { value: 'line', label: t('monitoring:qtm.chart.line'), icon: <FiTrendingUp size={13} /> },
  { value: 'vbar', label: t('monitoring:qtm.chart.vbar'), icon: <FiBarChart2 size={13} /> },
  { value: 'hbar', label: t('monitoring:qtm.chart.hbar'), icon: <FiBarChart2 size={13} style={{ transform: 'rotate(90deg)' }} /> },
];

const QueryTemplateManagerModal = ({ isOpen, onClose, onRegisterAsCriterion }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [templates, setTemplates] = useState([]);
  const [editorState, setEditorState] = useState({ open: false, template: null });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chartFilter, setChartFilter] = useState('all');  // 'all' | 'line' | 'vbar' | 'hbar'
  const [dbTypeFilter, setDbTypeFilter] = useState('all'); // 'all' | 'any' | 'oracle' | ...
  // 목록에서 고른 템플릿 — 오른쪽 칸에 내용을 보인다(2026-10-28 사용자 요청: 아이콘을 눌러 여는 창 대신)
  const [selectedId, setSelectedId] = useState(null);

  const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/sql-developer\/?$/, '');

  const loadTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/sql-developer/monitoring/query-templates`);
      if (!res.ok) return;
      const json = await res.json();
      setTemplates(json.data || []);
    } catch (err) {
      console.error('[QueryTemplateManagerModal] 로드 실패:', err);
    }
  }, [API_BASE]);

  useEffect(() => { if (isOpen) loadTemplates(); }, [isOpen, loadTemplates]);

  // 필터된 목록 — chartFilter + dbTypeFilter 모두 적용
  const filteredTemplates = templates.filter(t => {
    if (chartFilter !== 'all' && t.chartType !== chartFilter) return false;
    if (dbTypeFilter !== 'all' && (t.dbType || 'any') !== dbTypeFilter) return false;
    return true;
  });

  // 차트 타입별 개수 (필터 탭 배지용)
  const countByChart = templates.reduce((acc, t) => {
    acc[t.chartType] = (acc[t.chartType] || 0) + 1;
    return acc;
  }, {});

  // 신규 생성 시 현재 필터 chartType 을 기본값으로 prefill.
  const openCreate = () => {
    const defaultChartType = chartFilter === 'all' ? 'line' : chartFilter;
    setEditorState({ open: true, template: { chartType: defaultChartType } });
  };
  const openEdit = (tpl) => setEditorState({ open: true, template: tpl });
  const closeEditor = () => setEditorState({ open: false, template: null });

  const handleSave = async (payload) => {
    const editing = editorState.template;
    try {
      const url = editing?.id
        ? `${API_BASE}/sql-developer/monitoring/query-templates/${encodeURIComponent(editing.id)}`
        : `${API_BASE}/sql-developer/monitoring/query-templates`;
      const method = editing?.id ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { toast.error(await describeHttpError(res, t('monitoring:qtm.err.save'), t)); return; }
      await loadTemplates();
      toast.success(editing?.id ? t('monitoring:qtm.toast.saved') : t('monitoring:qtm.toast.registered', { name: payload.name }));
      closeEditor();
    } catch (err) {
      toast.error(t('monitoring:qtm.err.saveMsg', { message: err.message }));
    }
  };

  const handleDelete = async (tpl) => {
    if (!tpl?.id) return;
    const ok = await confirm({
      title: t('monitoring:qtm.deleteTitle'),
      message: t('monitoring:qtm.deleteConfirm', { name: tpl.name }),
      confirmText: t('monitoring:qtm.delete'),
      variant: 'danger',
    });
    if (!ok) return;
    try {
      const res = await fetch(`${API_BASE}/sql-developer/monitoring/query-templates/${encodeURIComponent(tpl.id)}`, { method: 'DELETE' });
      if (!res.ok) { toast.error(await describeHttpError(res, t('monitoring:qtm.err.delete'), t)); return; }
      await loadTemplates();
      toast.success(t('monitoring:qtm.toast.deleted'));
      closeEditor();
    } catch (err) {
      toast.error(t('monitoring:qtm.err.deleteMsg', { message: err.message }));
    }
  };

  const handleClose = useCallback(() => {
    setIsFullscreen(false);
    onClose?.();
  }, [onClose]);

  // 창처럼 — 끌어 이동 · 최대화 · 새 창으로 분리(다른 모니터에서 전체 화면). common/useModalWindow (2026-10-06)
  const win = useModalWindow({ title: t('monitoring:qtm.title'), maximized: isFullscreen, onToggleMaximize: () => setIsFullscreen(v => !v) });
  const { reset: resetWin } = win;
  useEffect(() => { if (!isOpen) resetWin(); }, [isOpen, resetWin]);   // 닫으면 다음엔 제자리·앱 안에서

  if (!isOpen) return null;

  return win.wrap(
    <div className={styles.overlay} style={win.overlayStyle}>
      <div className={`app-modal ${styles.modal} ${isFullscreen ? styles.modalFullscreen : ''}`} data-app-modal ref={win.frameRef} style={win.frameStyle}>
        <div className={`app-modal-header ${styles.header}`} {...win.headerProps}>
          <h2 className="app-modal-title"><FiFileText size={14} />{t('monitoring:qtm.title')}</h2>
          <div className={styles.headerControls}>
            <button className={`app-modal-iconbtn ${styles.refreshBtn}`} onClick={loadTemplates} title={t('monitoring:qtm.refresh')}>
              <FiRefreshCw size={16} />
            </button>
            {/* 앱 안 최대화 — 분리한 창에서는 그 창의 「전체 화면」(win.controls)만 둔다(같은 ⤢ 가 둘 서지 않게) */}
            {!win.popped && !win.placed && (
              <button
                className={`app-modal-iconbtn ${styles.fsBtn}`}
                onClick={() => setIsFullscreen(v => !v)}
                title={isFullscreen ? t('monitoring:qtm.exitFullscreen') : t('monitoring:qtm.fullscreen')}
              >
                {isFullscreen ? <FiMinimize2 size={16} /> : <FiMaximize2 size={16} />}
              </button>
            )}
            {win.controls}
            <button className={`app-modal-close ${styles.closeBtn}`} onClick={handleClose}>
              <FiX size={16} />
            </button>
          </div>
        </div>

        <div
          className={styles.body}
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',  /* 외부 스크롤 차단 — 내부 list 영역만 스크롤 */
            minHeight: 0,
            gap: 0,
          }}
        >
          <div
            className={styles.settingsTab}
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              padding: 0,        /* settingsTab 의 default padding 무력화 — flex 정렬에 영향 없게 */
            }}
          >
            {/* 고정: 상단 (제목, 설명, 필터) */}
            <div style={{ flexShrink: 0 }}>
              <h3>{t('monitoring:qtm.manage')}</h3>
              <p>
                {t('monitoring:qtm.intro1')}
                {t('monitoring:qtm.intro2')}
                {t('monitoring:qtm.builtinNote')}
              </p>

              {/* 차트 타입별 필터 탭 */}
              <div className={styles.chartFilterTabs}>
                {getChartFilterOptions(t).map(opt => {
                  const count = opt.value === 'all'
                    ? templates.length
                    : (countByChart[opt.value] || 0);
                  const active = chartFilter === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={`${styles.chartFilterTab} ${active ? styles.chartFilterTabActive : ''}`}
                      onClick={() => setChartFilter(opt.value)}
                    >
                      {opt.icon && <span style={{ display: 'inline-flex' }}>{opt.icon}</span>}
                      {opt.label}
                      <span className={styles.chartFilterTabCount}>{count}</span>
                    </button>
                  );
                })}
              </div>

              {/* DBMS 별 필터 (드롭다운) */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, fontSize: 12 }}>
                <span style={{ color: 'var(--color-text-muted, #6b7280)' }}>{t('monitoring:qtm.dbmsFilter')}</span>
                <select
                  value={dbTypeFilter}
                  onChange={(e) => setDbTypeFilter(e.target.value)}
                  style={{
                    padding: '4px 10px',
                    border: '1px solid var(--color-border, #d1d5db)',
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                >
                  {getDbTypeFilterOptions(t).map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* 가운데: 왼쪽 목록 · 오른쪽 내용(2026-10-28) — 목록 행을 누르면 그 템플릿의 내용이 오른쪽에 보인다 */}
            {(() => {
              const selected = filteredTemplates.find(x => x.id === selectedId) || null;
              // 수집 저장소(SQLite)를 읽는 템플릿은 쌓인 것을 보는 쿼리라 수집 기준(대상 DB 에서 모아 오는 쿼리)이 될 수 없다
              const canRegister = !!(selected && onRegisterAsCriterion && (selected.dbType || 'any') !== 'sqlite');
              return (
                <div className={styles.qtmSplit}>
                  <div className={styles.qtmList}>
                    <table className={styles.settingsTable}>
                      <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr>
                          <th>{t('monitoring:qtm.col.name')}</th>
                          <th style={{ width: 70 }}>{t('monitoring:qtm.col.chart')}</th>
                          <th style={{ width: 96 }}>DBMS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTemplates.length === 0 && (
                          <tr>
                            <td colSpan={3} style={{ color: 'var(--color-text-muted, #6b7280)', padding: 20, textAlign: 'center' }}>
                              {templates.length === 0 ? t('monitoring:qtm.empty') : t('monitoring:qtm.noMatch')}
                            </td>
                          </tr>
                        )}
                        {filteredTemplates.map(tpl => (
                          <tr
                            key={tpl.id}
                            className={`${styles.qtmRow} ${tpl.id === selectedId ? styles.qtmRowActive : ''}`}
                            onClick={() => setSelectedId(tpl.id)}
                          >
                            <td>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                {tpl.builtin ? <FiLock size={12} style={{ color: 'var(--color-text-muted, #6b7280)' }} /> : null}
                                <strong>{tpl.name}</strong>
                              </span>
                            </td>
                            <td><span className={styles.tabKindBadge}>{tpl.chartType}</span></td>
                            <td>
                              <span className={`${styles.tabKindBadge} ${(tpl.dbType || 'any') === 'any' ? '' : styles.kindCustom}`}>
                                {getDbTypeLabel(t)[tpl.dbType || 'any'] || tpl.dbType}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className={styles.qtmDetail}>
                    {!selected ? (
                      <div className={styles.qtmEmpty}>{t('monitoring:qtm.pickOne')}</div>
                    ) : (
                      <div key={selected.id} className={styles.qtmDetailBody}>
                        <div className={styles.qtmDetailHead}>
                          {selected.builtin ? <FiLock size={13} /> : null}
                          <strong>{selected.name}</strong>
                          <span className={styles.tabKindBadge}>{selected.chartType}</span>
                          <span className={styles.tabKindBadge}>{getDbTypeLabel(t)[selected.dbType || 'any'] || selected.dbType}</span>
                        </div>
                        {selected.description && <p className={styles.qtmDesc}>{selected.description}</p>}
                        {(selected.xField || (selected.yFields && selected.yFields.length > 0)) && (
                          <div className={styles.qtmFields}>
                            {selected.xField && <span>X: <code>{selected.xField}</code></span>}
                            {selected.yFields && selected.yFields.length > 0 && <span>Y: <code>{selected.yFields.join(', ')}</code></span>}
                          </div>
                        )}
                        <pre className={styles.qtmSql}>{selected.sql}</pre>
                        <div className={styles.qtmActions}>
                          <button type="button" className="app-btn" onClick={() => openEdit(selected)}>
                            {selected.builtin ? <FiEye size={13} /> : <FiEdit3 size={13} />} {selected.builtin ? t('monitoring:qtm.viewOnly') : t('monitoring:qtm.edit')}
                          </button>
                          {onRegisterAsCriterion && (
                            <button
                              type="button"
                              className="app-btn app-btn-primary"
                              disabled={!canRegister}
                              title={canRegister ? t('monitoring:qtm.toCriterionHint') : t('monitoring:qtm.toCriterionStore')}
                              onClick={() => onRegisterAsCriterion(selected)}
                            >
                              <FiPlus size={13} /> {t('monitoring:qtm.toCriterion')}
                            </button>
                          )}
                          {!selected.builtin && (
                            <button type="button" className="app-btn app-btn-danger" onClick={() => handleDelete(selected)}>
                              <FiTrash2 size={13} /> {t('monitoring:qtm.deleteTitle')}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* 고정: 하단 (액션 버튼) */}
            <div className={styles.settingsActions} style={{ flexShrink: 0 }}>
              <button className={styles.btnPrimary} onClick={openCreate}>
                <FiPlus size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {t('monitoring:qtm.addTitle')}
              </button>
            </div>
          </div>
        </div>

        <QueryTemplateEditor
          open={editorState.open}
          template={editorState.template}
          onClose={closeEditor}
          onSave={handleSave}
          onDelete={handleDelete}
        />
      </div>
    </div>
  );
};

export default QueryTemplateManagerModal;
